// ============================================================================
// SIR 2026 — parts list with voter totals
//
// One row per part, putting the three counts side by side so the gaps are
// visible at a glance:
//
//   Enumeration  — voters the SIR enumeration flagged in that part
//   2026 official — TotalCount printed in the draft-roll PDF's own header
//   2026 loaded   — rows actually imported into SIRDraftRoll
//
// "Official vs loaded" is the one that matters: a non-zero difference means the
// OCR import missed (or invented) rows for that part, and per docs/sir-draft-roll.md
// a miscounted part shifts vno and attaches one voter's status to another person.
// Official is blank for most parts because the header did not OCR.
//
// Fed entirely from the two part lists SirSearchPage already loads — no extra
// API call, so opening this costs nothing.
// ============================================================================

import React, { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { SIR_ASSEMBLY_NAME, SIR_ASSEMBLY_NO } from "./sirConfig";

const controlStyle = {
  height: 30,
  padding: "0 10px",
  borderRadius: 4,
  border: "1px solid #c7ced6",
  fontSize: 13,
  background: "#fff",
};

const num = (v) => (v == null || v === "" ? null : Number(v));

/** Blank rather than 0 for an unknown count — 0 would read as "no voters". */
const show = (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN"));

function SirPartsReport({ show: open, onClose, parts, draftParts }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("part");
  const [sortDir, setSortDir] = useState("asc");

  // Merge the two lists on part number. Either side can be missing a part:
  // the enumeration and the draft roll are independent imports.
  const merged = useMemo(() => {
    const byPart = new Map();

    (parts || []).forEach((p) => {
      const k = Number(p.yadibhag);
      byPart.set(k, {
        part: k,
        name: p.PartName || "",
        pin: p.PinCode || "",
        enumerated: num(p.voterCount),
        official: null,
        loaded: null,
        station: "",
      });
    });

    (draftParts || []).forEach((p) => {
      const k = Number(p.yadibhag);
      const row = byPart.get(k) || {
        part: k, name: "", pin: p.PinCode || "",
        enumerated: null, official: null, loaded: null, station: "",
      };
      row.station = p.PollingStation || p.StationAddress || row.station;
      row.official = num(p.TotalCount);
      row.loaded = num(p.loadedCount);
      if (!row.pin) row.pin = p.PinCode || "";
      byPart.set(k, row);
    });

    return Array.from(byPart.values()).map((r) => ({
      ...r,
      // Only meaningful when the header actually read.
      diff: r.official != null && r.loaded != null ? r.loaded - r.official : null,
    }));
  }, [parts, draftParts]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = merged;

    if (q) {
      list = list.filter(
        (r) =>
          String(r.part) === q ||
          String(r.part).startsWith(q) ||
          r.name.toLowerCase().includes(q) ||
          r.station.toLowerCase().includes(q) ||
          String(r.pin).startsWith(q)
      );
    }

    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      let x = a[sortBy];
      let y = b[sortBy];
      // Unknowns sort last whichever way the column is pointing, so they never
      // crowd out the rows the operator is actually looking for.
      if (x == null && y == null) return a.part - b.part;
      if (x == null) return 1;
      if (y == null) return -1;
      if (typeof x === "string") {
        x = x.toLowerCase();
        y = String(y).toLowerCase();
      }
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return a.part - b.part;
    });
  }, [merged, search, sortBy, sortDir]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          enumerated: acc.enumerated + (r.enumerated || 0),
          official: acc.official + (r.official || 0),
          loaded: acc.loaded + (r.loaded || 0),
          mismatched: acc.mismatched + (r.diff ? 1 : 0),
          unverified: acc.unverified + (r.official == null ? 1 : 0),
        }),
        { enumerated: 0, official: 0, loaded: 0, mismatched: 0, unverified: 0 }
      ),
    [rows]
  );

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir(col === "part" || col === "name" || col === "station" ? "asc" : "desc");
    }
  };

  const stamp = () => new Date().toISOString().slice(0, 10);

  const exportExcel = () => {
    const sheet = rows.map((r) => ({
      Part: r.part,
      "Part name": r.name,
      "Polling station": r.station,
      "Pin code": r.pin,
      "Enumeration voters": r.enumerated,
      "2026 official total": r.official,
      "2026 loaded rows": r.loaded,
      Difference: r.diff,
      Status: r.official == null ? "header unread" : r.diff ? "MISMATCH" : "OK",
    }));

    sheet.push({});
    sheet.push({
      Part: "TOTAL",
      "Enumeration voters": totals.enumerated,
      "2026 official total": totals.official,
      "2026 loaded rows": totals.loaded,
      Status: `${rows.length} parts, ${totals.mismatched} mismatched, ${totals.unverified} unverified`,
    });

    const ws = XLSX.utils.json_to_sheet(sheet);
    ws["!cols"] = [
      { wch: 6 }, { wch: 30 }, { wch: 46 }, { wch: 9 },
      { wch: 18 }, { wch: 18 }, { wch: 16 }, { wch: 11 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parts");
    XLSX.writeFile(wb, `sir-parts-${SIR_ASSEMBLY_NO}-${stamp()}.xlsx`);
  };

  const exportPdf = () => {
    // Landscape: nine columns including a polling-station name do not fit
    // portrait without truncating the one column that identifies the part.
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    doc.setFontSize(13);
    doc.text(`SIR 2026 — Parts and voter totals — AC ${SIR_ASSEMBLY_NAME}`, 40, 34);
    doc.setFontSize(9);
    doc.text(
      `${rows.length} parts · enumeration ${totals.enumerated.toLocaleString("en-IN")} · ` +
        `2026 loaded ${totals.loaded.toLocaleString("en-IN")} · ` +
        `${totals.mismatched} mismatched · ${totals.unverified} header unread · ${stamp()}`,
      40,
      48
    );

    autoTable(doc, {
      startY: 60,
      head: [[
        "Part", "Part name", "Polling station", "Pin",
        "Enum.", "2026 official", "2026 loaded", "Diff", "Status",
      ]],
      body: rows.map((r) => [
        r.part,
        r.name,
        r.station,
        r.pin,
        show(r.enumerated),
        show(r.official),
        show(r.loaded),
        r.diff == null ? "-" : r.diff > 0 ? `+${r.diff}` : String(r.diff),
        r.official == null ? "header unread" : r.diff ? "MISMATCH" : "OK",
      ]),
      foot: [[
        "TOTAL", "", "", "",
        show(totals.enumerated), show(totals.official), show(totals.loaded), "", "",
      ]],
      styles: { fontSize: 7.5, cellPadding: 2.5, overflow: "linebreak" },
      headStyles: { fillColor: [23, 63, 107], textColor: 255, fontSize: 7.5 },
      footStyles: { fillColor: [233, 236, 239], textColor: 20, fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 30, halign: "right" },
        1: { cellWidth: 130 },
        2: { cellWidth: 250 },
        3: { cellWidth: 38 },
        4: { cellWidth: 46, halign: "right" },
        5: { cellWidth: 62, halign: "right" },
        6: { cellWidth: 58, halign: "right" },
        7: { cellWidth: 38, halign: "right" },
        8: { cellWidth: 62 },
      },
      // Draw attention to the rows that need investigating, not the healthy ones.
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const r = rows[data.row.index];
        if (r && r.diff) data.cell.styles.fillColor = [255, 235, 235];
      },
    });

    doc.save(`sir-parts-${SIR_ASSEMBLY_NO}-${stamp()}.pdf`);
  };

  if (!open) return null;

  const th = (label, col, extra) => (
    <th
      onClick={() => toggleSort(col)}
      title="Click to sort"
      style={{
        textAlign: extra?.right ? "right" : "left",
        padding: "8px 6px",
        borderBottom: "1px solid #dfe4ea",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {label}
      {sortBy === col ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 6,
          width: "min(1200px, 100%)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 12px",
            borderBottom: "1px solid #dfe4ea",
            flexWrap: "wrap",
          }}
        >
          <b style={{ fontSize: 14 }}>Parts and voter totals</b>
          <span style={{ fontSize: 12, color: "#5a6672" }}>
            {rows.length} parts · {totals.loaded.toLocaleString("en-IN")} loaded
          </span>

          {totals.mismatched > 0 && (
            <span
              style={{
                background: "#fff3cd",
                color: "#7a5c00",
                padding: "2px 8px",
                borderRadius: 10,
                fontSize: 11.5,
                fontWeight: 700,
              }}
              title="Loaded rows do not match the count printed in the PDF header"
            >
              {totals.mismatched} mismatched
            </span>
          )}

          <span style={{ flex: 1 }} />

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Part no, name, station or pin"
            style={{ ...controlStyle, width: 230 }}
          />
          <button
            type="button"
            onClick={exportExcel}
            style={{ ...controlStyle, cursor: "pointer", fontWeight: 600 }}
          >
            Export Excel
          </button>
          <button
            type="button"
            onClick={exportPdf}
            style={{ ...controlStyle, cursor: "pointer", fontWeight: 600 }}
          >
            Export PDF
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{ ...controlStyle, cursor: "pointer" }}
          >
            Close
          </button>
        </div>

        <div style={{ overflow: "auto", padding: "0 12px 12px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#f1f4f7", zIndex: 1 }}>
                {th("Part", "part")}
                {th("Part name", "name")}
                {th("Polling station", "station")}
                {th("Enum.", "enumerated", { right: true })}
                {th("2026 official", "official", { right: true })}
                {th("2026 loaded", "loaded", { right: true })}
                {th("Diff", "diff", { right: true })}
              </tr>
            </thead>

            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={7} style={{ padding: 16, color: "#5a6672" }}>
                    No parts match that search.
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr
                  key={r.part}
                  style={{
                    borderBottom: "1px solid #eef1f4",
                    background: r.diff ? "#fff5f5" : "transparent",
                  }}
                >
                  <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 700 }}>
                    {r.part}
                  </td>
                  <td style={{ padding: "5px 6px" }} title={r.name}>
                    {r.name || "-"}
                  </td>
                  <td
                    style={{
                      padding: "5px 6px",
                      maxWidth: 340,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={r.station}
                  >
                    {r.station || "-"}
                  </td>
                  <td style={{ padding: "5px 6px", textAlign: "right" }}>
                    {show(r.enumerated)}
                  </td>
                  <td
                    style={{ padding: "5px 6px", textAlign: "right", color: r.official == null ? "#98a2ad" : "inherit" }}
                    title={r.official == null ? "The PDF header did not OCR, so there is nothing to check against" : ""}
                  >
                    {show(r.official)}
                  </td>
                  <td style={{ padding: "5px 6px", textAlign: "right", fontWeight: 600 }}>
                    {show(r.loaded)}
                  </td>
                  <td
                    style={{
                      padding: "5px 6px",
                      textAlign: "right",
                      fontWeight: r.diff ? 700 : 400,
                      color: r.diff ? "#a01722" : "#98a2ad",
                    }}
                  >
                    {r.diff == null ? "-" : r.diff > 0 ? `+${r.diff}` : r.diff}
                  </td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr style={{ background: "#f1f4f7", fontWeight: 700 }}>
                <td style={{ padding: "8px 6px", textAlign: "right" }}>TOTAL</td>
                <td colSpan={2} style={{ padding: "8px 6px", fontWeight: 400, color: "#5a6672" }}>
                  {totals.unverified} part(s) have no header count to check against
                </td>
                <td style={{ padding: "8px 6px", textAlign: "right" }}>
                  {show(totals.enumerated)}
                </td>
                <td style={{ padding: "8px 6px", textAlign: "right" }}>
                  {show(totals.official)}
                </td>
                <td style={{ padding: "8px 6px", textAlign: "right" }}>
                  {show(totals.loaded)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: 8, fontSize: 11.5, color: "#5a6672", lineHeight: 1.5 }}>
            <b>Enum.</b> = voters the SIR enumeration flagged in that part.{" "}
            <b>2026 official</b> = the total printed in the draft-roll PDF header (blank where the
            header did not OCR).{" "}
            <b>2026 loaded</b> = rows imported into the roll. A non-zero{" "}
            <b>Diff</b> means the import missed or duplicated rows for that part, so its voter
            numbers should not be trusted until it is re-checked.
          </div>
        </div>
      </div>
    </div>
  );
}

export default SirPartsReport;
